import type { Translate } from "@similarweb/i18n-provider";
import { type ColumnsType, TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ItemIcon, ItemIconChain } from "@similarweb/ui-components";
import type { TextProps } from "@similarweb/ui-components/foundations";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import type { ICustomSegmentMember } from "app/services/segments/SegmentsUtils";
import { SegmentTypeBadge } from "pro-features/pages/segments/StyledComponents";

import { MAX_VISIBLE_COMPARISON_MEMBER_ICONS } from "./constants";
import type { ComparisonRow, RowActionRenderer } from "./types";

interface IGetComparisonColumnsParams {
    rowActionRenderers: RowActionRenderer[];
    translate: Translate;
}

const TruncatedText = (props: TextProps) => (
    <Text
        {...props}
        width="100%"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
    >
        {props.children}
    </Text>
);

export const getComparisonColumns = ({
    rowActionRenderers,
    translate,
}: IGetComparisonColumnsParams): ColumnsType<ComparisonRow> => [
    {
        key: "name",
        dataIndex: "name",
        title: "",
        sorter: (a: ComparisonRow, b: ComparisonRow) => a.name.localeCompare(b.name),
        render: (value: string) => <TextCell>{value}</TextCell>,
    },
    {
        key: "comparisonMembers",
        dataIndex: "comparisonMembers",
        title: "",
        render: (value: ICustomSegmentMember[], row: ComparisonRow) => {
            if (!value?.length) {
                return null;
            }

            const [primarySegment, ...restSegments] = value;
            const isPrimaryWebsite =
                "type" in primarySegment && `${primarySegment.type}` === `${SEGMENT_TYPES.WEBSITE}`;

            return (
                <Flex
                    width="100%"
                    alignItems="center"
                    gap="8px"
                    justifyContent="space-between"
                >
                    <Box
                        minWidth="0"
                        flex="1"
                    >
                        <Flex
                            alignItems="center"
                            gap="12px"
                        >
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                <ItemIcon iconSrc={primarySegment?.favicon} />
                                <Stack width="100px">
                                    <TruncatedText
                                        fontVariant="input2"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {primarySegment?.domain}
                                    </TruncatedText>
                                    {isPrimaryWebsite ? (
                                        <SegmentTypeBadge>WEBSITE</SegmentTypeBadge>
                                    ) : (
                                        <TruncatedText
                                            fontVariant="listItem3"
                                            color={colorsPalettes.carbon[400]}
                                        >
                                            {"segmentName" in primarySegment ? primarySegment.segmentName : ""}
                                        </TruncatedText>
                                    )}
                                </Stack>
                            </Flex>
                            <Text
                                flexShrink="0"
                                fontVariant="body2"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate("segment.startpage.comparision.versus")}
                            </Text>
                            <ItemIconChain
                                icons={restSegments.map((segment) => segment?.favicon)}
                                maxIconChain={MAX_VISIBLE_COMPARISON_MEMBER_ICONS}
                            />
                        </Flex>
                    </Box>
                    {rowActionRenderers.length > 0 ? (
                        <div
                            className="comparison-row-actions"
                            onClick={(event) => event.stopPropagation()}
                        >
                            {rowActionRenderers.map((Renderer, index) => (
                                <Renderer
                                    key={index}
                                    row={row}
                                />
                            ))}
                        </div>
                    ) : null}
                </Flex>
            );
        },
    },
];
